"use client";
import React from "react";
import { motion, MotionValue } from "motion/react";
import Link from "next/link";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUpRight01Icon } from "@hugeicons/core-free-icons";
import { ProjectType } from "@/components/utils/types";

interface HeroProps {
  project: ProjectType;
  yHero: MotionValue<string>;
  opacityHero: MotionValue<number>;
}

export const Hero = ({ project, yHero, opacityHero }: HeroProps) => {
  return (
    <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden">
      <motion.div
        style={{ y: yHero, opacity: opacityHero }}
        className="z-10 flex flex-col items-center"
      >
        <motion.h1
          animate={{ y: [0, -20, 0] }}
          transition={{
            duration: 8,
            repeat: Infinity,
            type: "tween",
          }}
          className="font-recoleta font-extrabold tracking-wide text-[15vw] leading-[0.8] bg-linear-to-b from-sky-700 via-blue-300 to-transparent dark:from-sky-300 dark:via-blue-600 dark:to-transparent bg-clip-text text-transparent px-4 text-center"
        >
          {project.name}
        </motion.h1>
        <p className="mt-4 font-outfit capitalize px-4 max-w-sm md:max-w-xl lg:max-w-2xl text-center text-sm md:text-xl text-black/50 dark:text-white/50">
          {project.description}
        </p>

        <motion.a
          href={project.url}
          target="_blank"
          animate={{ y: [0, 20, 0] }}
          transition={{
            duration: 8,
            repeat: Infinity,
            type: "tween",
          }}
          className="mt-8"
        >
          <div
            rel="noopener noreferrer"
            className="group relative inline-flex items-center gap-2 px-8 py-4 bg-linear-to-r dark:bg-linear-to-tr dark:hover:bg-linear-to-tl from-sky-900 dark:from-sky-500 to-blue-600 dark:to-blue-900 text-white rounded-full text-base md:text-lg font-medium tracking-wide overflow-hidden transition-all duration-1000 ease-in-out hover:scale-105 shadow-lg shadow-blue-500/25 cursor-pointer"
          >
            <span>Launch App</span>
            <HugeiconsIcon icon={ArrowUpRight01Icon} className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        </motion.a>
      </motion.div>

      {/* Abstract/Background decorations could go here */}
      <div className="absolute inset-0 bg-linear-to-b from-transparent to-[#f5f5f7] dark:to-[#050505] z-0 pointer-events-none h-full w-full" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 20,
          delay: 0.8,
        }}
        className="absolute bottom-10 flex flex-col items-center gap-2 z-20"
        style={{ opacity: opacityHero }}
      >
        <span className="text-[10px] font-outfit uppercase tracking-[0.2em] text-black/30 dark:text-white/30">
          Scroll to explore
        </span>
      </motion.div>
    </section>
  );
};
